// Role permissions editor (legacy RolePermissionComponent):
//   GET roles/{id}?permissions=yes → { name, permissions: string[], all_permissions: string[] }
//   POST roles/{id}/permissions with body = string[] of granted permission names
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Checkbox, Divider, FormControlLabel, Stack, Typography } from '@mui/material';
import { ArrowLeft } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage } from '../../api/client';
import { LoadingBlock } from '../../components/StateBlock';
import { SectionHeader } from '../../dashboard/components/SectionHeader';
import { hasTranslation, t } from '../../i18n';

// Same grouping and order as the legacy screen.
export const PERMISSION_GROUPS: Array<{ key: string; permissions: string[] }> = [
  { key: 'pos', permissions: ['read-pos', 'create-orders', 'update-orders', 'delete-orders'] },
  { key: 'sales', permissions: ['read-sales', 'create-sales', 'update-sales', 'delete-sales', 'email-sales'] },
  { key: 'deliveries', permissions: ['read-deliveries', 'create-deliveries', 'update-deliveries', 'delete-deliveries', 'email-deliveries'] },
  { key: 'recurringSales', permissions: ['read-recurring-sales', 'create-recurring-sales', 'update-recurring-sales', 'delete-recurring-sales', 'email-recurring-sales'] },
  { key: 'customers', permissions: ['read-customers', 'create-customers', 'update-customers', 'delete-customers'] },
  { key: 'purchases', permissions: ['read-purchases', 'create-purchases', 'update-purchases', 'delete-purchases', 'email-purchases'] },
  { key: 'suppliers', permissions: ['read-suppliers', 'create-suppliers', 'update-suppliers', 'delete-suppliers'] },
  { key: 'quotations', permissions: ['read-quotations', 'create-quotations', 'update-quotations', 'delete-quotations'] },
  { key: 'returnOrders', permissions: ['read-return-orders', 'create-return-orders', 'update-return-orders', 'delete-return-orders'] },
  { key: 'payments', permissions: ['read-payments', 'create-payments', 'update-payments', 'delete-payments', 'email-payments'] },
  { key: 'incomes', permissions: ['read-incomes', 'create-incomes', 'update-incomes', 'delete-incomes'] },
  { key: 'expenses', permissions: ['read-expenses', 'create-expenses', 'update-expenses', 'delete-expenses'] },
  { key: 'items', permissions: ['read-items', 'create-items', 'update-items', 'delete-items', 'label-items', 'trail-items'] },
  { key: 'modifiers', permissions: ['read-modifiers', 'create-modifiers', 'update-modifiers', 'delete-modifiers'] },
  { key: 'giftCards', permissions: ['read-gift-cards', 'create-gift-cards', 'update-gift-cards', 'delete-gift-cards', 'log-gift-cards'] },
  { key: 'stockAdjustments', permissions: ['read-stock-adjustments', 'create-stock-adjustments', 'update-stock-adjustments', 'delete-stock-adjustments'] },
  { key: 'assetTransfers', permissions: ['read-asset-transfers', 'create-asset-transfers', 'update-asset-transfers', 'delete-asset-transfers'] },
  { key: 'stockTransfers', permissions: ['read-stock-transfers', 'create-stock-transfers', 'update-stock-transfers', 'delete-stock-transfers'] },
  { key: 'accounts', permissions: ['read-accounts', 'create-accounts', 'update-accounts', 'delete-accounts'] },
  { key: 'locations', permissions: ['read-locations', 'create-locations', 'update-locations', 'delete-locations'] },
  { key: 'users', permissions: ['read-users', 'create-users', 'update-users', 'delete-users'] },
  { key: 'roles', permissions: ['read-roles', 'create-roles', 'update-roles', 'delete-roles'] },
  { key: 'salaries', permissions: ['read-salaries', 'create-salaries', 'update-salaries', 'delete-salaries'] },
  { key: 'promos', permissions: ['read-promos', 'create-promos', 'update-promos', 'delete-promos'] },
  { key: 'brands', permissions: ['read-brands', 'create-brands', 'update-brands', 'delete-brands'] },
  { key: 'categories', permissions: ['read-categories', 'create-categories', 'update-categories', 'delete-categories'] },
  { key: 'taxes', permissions: ['read-taxes', 'create-taxes', 'update-taxes', 'delete-taxes'] },
  { key: 'fields', permissions: ['read-fields', 'create-fields', 'update-fields', 'delete-fields'] },
  { key: 'customerGroups', permissions: ['read-customer-groups', 'create-customer-groups', 'update-customer-groups', 'delete-customer-groups'] },
  { key: 'halls', permissions: ['read-halls', 'create-halls', 'update-halls', 'delete-halls'] },
  { key: 'tables', permissions: ['read-tables', 'create-tables', 'update-tables', 'delete-tables'] },
  { key: 'units', permissions: ['read-units', 'create-units', 'update-units', 'delete-units'] },
  {
    key: 'reports',
    permissions: [
      'read-report',
      'registers-report',
      'expiry-alerts-report',
      'quantity-alerts-report',
      'items-report',
      'sales-report',
      'purchases-report',
      'payments-report',
      'expenses-report',
      'incomes-report',
      'taxes-report',
      'stock-transfers-report',
      'stock-adjustments-report',
      'time-clock-report',
      'activity-logs-report'
    ]
  },
  { key: 'events', permissions: ['calendar', 'read-events', 'create-events', 'update-events', 'delete-events'] },
  { key: 'misc', permissions: ['alerts-report', 'delete-media', 'item-label-design'] }
];

// "read-recurring-sales" → action "read"; "registers-report" → "registers report".
function permissionLabel(name: string) {
  const [verb] = name.split('-');
  if (['read', 'create', 'update', 'delete', 'email', 'label', 'trail', 'log'].includes(verb)) {
    return t(`parties.perm.${verb}`);
  }
  const key = `parties.permName.${name}`;
  return hasTranslation(key) ? t(key) : name.replace(/-/g, ' ');
}

export function RolePermissionsPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState<{ name?: string; permissions?: string[]; all_permissions?: string[] } | null>(null);
  const [granted, setGranted] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setLoading(true);
    adminApi
      .get(`roles/${id}`, { params: { permissions: 'yes' } })
      .then((response) => {
        setRole(response.data);
        setGranted(new Set(Array.isArray(response.data?.permissions) ? response.data.permissions : []));
      })
      .catch((err) => setMessage({ text: apiErrorMessage(err, t('common.unableToLoad', { name: t('parties.permissions') })), severity: 'error' }))
      .finally(() => setLoading(false));
  }, [id]);

  const groups = useMemo(() => {
    const known = new Set(PERMISSION_GROUPS.flatMap((group) => group.permissions));
    const others = (role?.all_permissions ?? []).filter((name) => !known.has(name));
    return others.length ? [...PERMISSION_GROUPS, { key: 'other', permissions: others }] : PERMISSION_GROUPS;
  }, [role]);

  const toggle = (name: string, on: boolean) =>
    setGranted((current) => {
      const next = new Set(current);
      if (on) next.add(name);
      else next.delete(name);
      return next;
    });

  const toggleGroup = (permissions: string[], on: boolean) =>
    setGranted((current) => {
      const next = new Set(current);
      permissions.forEach((name) => (on ? next.add(name) : next.delete(name)));
      return next;
    });

  const save = async (stay: boolean) => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await adminApi.post(`roles/${id}/permissions`, Array.from(granted));
      if (!response.data?.success) throw new Error(t('common.saveFailed'));
      if (stay) setMessage({ text: t('parties.recordUpdated'), severity: 'success' });
      else navigate('/roles');
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, t('common.saveFailed')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <SectionHeader title={`${t('parties.editPermissions')}${role?.name ? ` — ${role.name}` : ''}`} description={t('parties.permissionsDescription')} />
      <Box>
        <Button size="small" startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/roles')}>
          {t('parties.backToRoles')}
        </Button>
      </Box>
      {message ? <Alert severity={message.severity}>{message.text}</Alert> : null}
      {loading ? (
        <LoadingBlock />
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' }, gap: 2 }}>
            {groups.map((group) => {
              const checkedCount = group.permissions.filter((name) => granted.has(name)).length;
              const all = checkedCount === group.permissions.length;
              return (
                <Card key={group.key} variant="outlined">
                  <CardContent>
                    <FormControlLabel
                      control={<Checkbox checked={all} indeterminate={checkedCount > 0 && !all} onChange={(e) => toggleGroup(group.permissions, e.target.checked)} />}
                      label={
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {t(`parties.permGroup.${group.key}`)}
                        </Typography>
                      }
                    />
                    <Divider sx={{ my: 1 }} />
                    <Stack>
                      {group.permissions.map((name) => (
                        <FormControlLabel
                          key={name}
                          control={<Checkbox size="small" checked={granted.has(name)} onChange={(e) => toggle(name, e.target.checked)} />}
                          label={
                            <Typography variant="body2">
                              {permissionLabel(name)} <Typography component="span" variant="caption" color="text.secondary">({name})</Typography>
                            </Typography>
                          }
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" disabled={saving} onClick={() => void save(false)}>
              {t('action.save')}
            </Button>
            <Button variant="outlined" disabled={saving} onClick={() => void save(true)}>
              {t('parties.saveAndStay')}
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}
